import { type SidebarEntry } from '../modules/sidebar/SidebarBuilder'
import { findSidebarEntry } from '../modules/sidebar/findSidebarEntry'

/**
 * Unified Breadcrumb System for NotePlan Web
 *
 * This system provides consistent breadcrumb functionality across all components.
 * It uses the sidebar entry approach for maximum consistency with the UI, ensuring
 * breadcrumbs always match what users see in the sidebar.
 *
 * Key features:
 * - Uses sidebar entries (not cache queries) for performance and consistency
 * - Handles both CloudKit and Supabase note structures
 * - Flexible output formats (array or string)
 * - Configurable exclude lists and root inclusion
 * - Automatic null/undefined handling
 *
 * Usage patterns:
 * - React components: Use useBreadcrumb() hook from '../hooks/useBreadcrumb'
 * - Utility functions: Use getBreadcrumbArray() for reusable functions
 * - Legacy compatibility: buildBreadcrumbPath() for string output
 *
 * @example
 * ```tsx
 * // In React components
 * const breadcrumbItems = useBreadcrumb(note.recordName)
 *
 * // In utility functions
 * const breadcrumbPath = useCallback((recordName: string) => {
 *   return getBreadcrumbArray(sidebarEntries, recordName)
 * }, [sidebarEntries])
 *
 * // Custom options
 * const breadcrumb = useBreadcrumb(recordName, {
 *   includeRoot: true,
 *   excludeFolders: ['temp']
 * })
 * ```
 */

/**
 * Options for breadcrumb generation
 */
export interface BreadcrumbOptions {
  /** Whether to include root folders like 'notes', 'teamspaces' */
  includeRoot?: boolean
  /** Additional folders to exclude from breadcrumb */
  excludeFolders?: string[]
  /** Format for output - array returns string[], string returns joined path */
  format?: 'array' | 'string'
  /** Separator for string format (default: '/') */
  separator?: string
}

/**
 * Unified breadcrumb utility that uses sidebar entries for consistency with UI.
 * This replaces all existing breadcrumb implementations with a single, reliable approach.
 *
 * @param sidebarEntries - The sidebar entries to traverse
 * @param recordName - The record name to build breadcrumb for
 * @param options - Options for breadcrumb generation
 * @returns Breadcrumb path as string[] or string based on format option
 */
export function buildUnifiedBreadcrumb(
  sidebarEntries: SidebarEntry[],
  recordName: string,
  options: BreadcrumbOptions = {}
): string[] | string {
  const {
    includeRoot = false,
    excludeFolders = [],
    format = 'array',
    separator = '/',
  } = options

  // Default system folders to exclude (can be overridden with includeRoot)
  const defaultExcludeFolders = includeRoot
    ? []
    : ['notes', 'teamspaces', 'smart-folders']

  const allExcludeFolders = [...defaultExcludeFolders, ...excludeFolders]

  /**
   * Recursively build breadcrumb path using sidebar entries
   */
  function buildBreadcrumbRecursive(currentRecordName: string): string[] {
    const path: string[] = []
    const { parent } = findSidebarEntry(sidebarEntries, currentRecordName)

    if (parent && !allExcludeFolders.includes(parent.recordName)) {
      // Recursively get parent path first
      path.push(...buildBreadcrumbRecursive(parent.recordName))
      // Then add current parent's title
      path.push(parent.title)
    }

    return path.filter((p) => p.length > 0)
  }

  const breadcrumbArray = buildBreadcrumbRecursive(recordName)

  return format === 'string' ? breadcrumbArray.join(separator) : breadcrumbArray
}

/**
 * Legacy support: Builds breadcrumb compatible with existing buildBreadcrumb function.
 * This maintains backward compatibility while using the unified approach.
 *
 * @deprecated Use buildUnifiedBreadcrumb instead
 */
export function buildBreadcrumbFromSidebar(
  sidebarEntries: SidebarEntry[],
  recordName: string
): string[] {
  return buildUnifiedBreadcrumb(sidebarEntries, recordName, {
    format: 'array',
    includeRoot: false,
  }) as string[]
}

/**
 * Legacy support: Builds breadcrumb path compatible with NoteTitle.tsx breadcrumbPath.
 * This maintains backward compatibility while using the unified approach.
 *
 * @deprecated Use buildUnifiedBreadcrumb instead
 */
export function buildBreadcrumbPath(
  sidebarEntries: SidebarEntry[] | undefined,
  recordName: string
): string {
  if (!sidebarEntries) {
    return ''
  }
  return buildUnifiedBreadcrumb(sidebarEntries, recordName, {
    format: 'string',
    includeRoot: false,
  }) as string
}

/**
 * Simplified helper for getting breadcrumb array from sidebar entries.
 * Useful for cases where you need a reusable function (like in filters).
 *
 * @param sidebarEntries - The sidebar entries to traverse
 * @param recordName - The record name to build breadcrumb for
 * @returns Array of breadcrumb segments
 */
export function getBreadcrumbArray(
  sidebarEntries: SidebarEntry[] | undefined,
  recordName: string
): string[] {
  if (!sidebarEntries) {
    return []
  }
  const result = buildUnifiedBreadcrumb(sidebarEntries, recordName, {
    format: 'array',
  })
  return Array.isArray(result) ? result : []
}
